import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Beaker, Home as HomeIcon, Clock, ShoppingCart, Check, FlaskConical } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const LabTestDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [t, setT] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/lab-tests/${slug}`).then((r) => setT(r.data)).catch(() => setT(null)).finally(() => setLoading(false));
    }, [slug]);

    const addAndGo = () => {
        if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
        const existing = JSON.parse(sessionStorage.getItem("lab-cart") || "[]");
        const key = `test-${t.id}`;
        const next = existing.some((x) => x.key === key)
            ? existing
            : [...existing, { key, test_id: t.id, name: t.name, price: t.selling_price }];
        sessionStorage.setItem("lab-cart", JSON.stringify(next));
        navigate("/lab-tests/book");
    };

    if (loading) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Loading…</main></div>);
    if (!t) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Test not found. <Link to="/lab-tests" className="text-brand-700 hover:underline">Browse tests</Link></main></div>);

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-4xl px-6 py-10 lg:px-12" data-testid="lab-test-detail">
                <Link to="/lab-tests" className="text-xs text-zinc-500 hover:underline">← All lab tests</Link>
                <div className="mt-3 grid gap-8 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                        <p className="label-uppercase text-zinc-500">{t.category}</p>
                        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>{t.name}</h1>
                        <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300">{t.description}</p>

                        <div className="mt-6 grid gap-3 sm:grid-cols-2">
                            <div className="rounded-2xl border border-zinc-100 p-4 dark:border-white/10">
                                <p className="flex items-center gap-1.5 text-xs font-medium text-zinc-500"><Clock className="h-3.5 w-3.5" /> Report</p>
                                <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-50">{t.reporting_time}</p>
                            </div>
                            <div className="rounded-2xl border border-zinc-100 p-4 dark:border-white/10">
                                <p className="flex items-center gap-1.5 text-xs font-medium text-zinc-500">
                                    {t.home_collection_available ? <HomeIcon className="h-3.5 w-3.5 text-emerald-600" /> : <Beaker className="h-3.5 w-3.5" />}
                                    Collection
                                </p>
                                <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-50">{t.home_collection_available ? "Home or Lab" : "Lab visit"}</p>
                            </div>
                            {t.fasting_required && (
                                <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4 sm:col-span-2">
                                    <p className="text-xs font-medium text-amber-800">⚠ Fasting required ({t.fasting_hours ? `${t.fasting_hours} hours` : "as advised"}) before this test.</p>
                                </div>
                            )}
                        </div>

                        {Array.isArray(t.parameters) && t.parameters.length > 0 && (
                            <section className="mt-8">
                                <p className="label-uppercase text-zinc-500">What this test measures</p>
                                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                                    {t.parameters.map((p) => (
                                        <li key={p} className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{p}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {t.preparation && (
                            <section className="mt-8">
                                <p className="label-uppercase text-zinc-500">Preparation</p>
                                <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">{t.preparation}</p>
                            </section>
                        )}
                    </div>

                    <aside className="space-y-4">
                        <div className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03] dark:border-white/10">
                            <FlaskConical className="h-5 w-5 text-brand-700" />
                            <p className="mt-2">
                                {t.mrp > t.selling_price && <span className="mr-2 text-sm line-through text-zinc-400">{inr(t.mrp)}</span>}
                                <span className="text-2xl font-semibold text-zinc-900 dark:text-zinc-50">{inr(t.selling_price)}</span>
                            </p>
                            {t.mrp > t.selling_price && <p className="text-xs text-emerald-700">Save {inr(t.mrp - t.selling_price)} ({Math.round(((t.mrp - t.selling_price) * 100) / t.mrp)}%)</p>}
                            <button onClick={addAndGo} className="btn-brand mt-4 w-full" data-testid="lab-test-detail-book">
                                <ShoppingCart className="mr-1.5 inline h-4 w-4" /> Book this test
                            </button>
                            <p className="mt-2 text-[11px] text-zinc-500">Free home collection • Reports via email & in-app.</p>
                        </div>
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default LabTestDetail;
